import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Chip, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppSkeleton } from 'src/component/common/analog/molecule/AppSkelton'
import { Caption } from 'src/feature/localization/component/atom/Caption'
import { appRoutePath } from 'src/feature/router/util/app-route-path'
import { SkillGapInsightsResponse } from '../../model/my-team-skill-gaps'
import { getGapStatus, getColorByRating } from '../../util/skillGapUtils'
import { observer } from 'mobx-react-lite'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'

export type InsightsTableProps = {
  insightsData?: SkillGapInsightsResponse
  isLoading: boolean
}

export const InsightsTable = observer(({ insightsData, isLoading }: InsightsTableProps) => {
  const navigate = useNavigate()
  const { translation } = useLocalizationStore()
  
  const pageTranslation = translation.pages.myTeam.skillGapInsights
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())

  const toggleExpandedRow = useCallback((userId: string, e: React.MouseEvent) => {
    e.stopPropagation()

    setExpandedRows((prev) => {
      const newSet = new Set(prev)

      if (newSet.has(userId)) {
        newSet.delete(userId)
      } else {
        newSet.add(userId)
      }

      return newSet
    })
  }, [])

  if (isLoading) {
    return (
      <AppSkeleton
        count={1}
        direction="column"
        rects={[
          { width: '100%', height: 40 },
          { width: '100%', height: 'calc(100vh - 200px)' },
        ]}
      />
    )
  }

  if (!insightsData?.users || insightsData.users.length === 0) {
    return (
      <AppBox sx={styles.emptyStateContainer}>
        <Caption sx={styles.emptyText}>
          {pageTranslation.emptyState()}
        </Caption>
      </AppBox>
    )
  }

  return (
    <AppBox sx={styles.tableContainer}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell><Typography sx={{ fontSize: 11, fontWeight: 500, color: 'text.secondary' }}>{pageTranslation.name()}</Typography></TableCell>
            <TableCell><Typography sx={{ fontSize: 11, fontWeight: 500, color: 'text.secondary' }}>{pageTranslation.skills()}</Typography></TableCell>
            <TableCell><Typography sx={{ fontSize: 11, fontWeight: 500, color: 'text.secondary' }}>{pageTranslation.ratingAvg()}</Typography></TableCell>
            <TableCell><Typography sx={{ fontSize: 11, fontWeight: 500, color: 'text.secondary' }}>{pageTranslation.expectedAvg()}</Typography></TableCell>
            <TableCell><Typography sx={{ fontSize: 11, fontWeight: 500, color: 'text.secondary' }}>{pageTranslation.gap()}</Typography></TableCell>
            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}><Typography sx={{ fontSize: 11, fontWeight: 500, color: 'text.secondary' }}>{pageTranslation.status()}</Typography></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {insightsData.users.map((user) => {
            const gap = user.expectedRating - user.avgRating
            const { status, color: gapColor } = getGapStatus(gap)

            return (
              <TableRow
                key={user.id}
                hover
                onClick={() => navigate(appRoutePath.MyTeamUserDetails(user.id), {
                  state: {
                    initialUserMeta: {
                      userId: user.id,
                      name: user.name || '',
                      email: '',
                      avatarUrl: '',
                      memberSince: '',
                    },
                  },
                })}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell><Typography sx={{ fontSize: { xs: 11, sm: 12 } }}>{user.name}</Typography></TableCell>
                <TableCell>
                  <AppBox sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {(expandedRows.has(user.id) ? user.skills : user.skills.slice(0, 3)).map(skill => (
                      <Typography key={skill.id} sx={{ fontSize: 11, color: 'text.secondary' }}>
                        {skill.skillName}
                      </Typography>
                    ))}
                    {user.skills.length > 3 && (
                      <Typography
                        onClick={e => toggleExpandedRow(user.id, e)}
                        sx={{
                          fontSize: 11,
                          color: 'primary.main',
                          cursor: 'pointer',
                          fontWeight: 500,
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        {expandedRows.has(user.id) ? pageTranslation.showLess() : `+${user.skills.length - 3} ${pageTranslation.more()}`}
                      </Typography>
                    )}
                  </AppBox>
                </TableCell>
                <TableCell>
                  <AppBox sx={{ display: 'flex', gap: { xs: 2, sm: 3 }, alignItems: 'center' }}>
                    {[1, 2, 3, 4, 5].map((star) => {
                      const isSelected = star === Math.floor(user.avgRating)

                      return (
                        <AppBox
                          key={star}
                          sx={{
                            width: { xs: 25, sm: 30 },
                            height: isSelected ? { xs: 18, sm: 20 } : { xs: 12, sm: 15 },
                            borderRadius: '10px',
                            backgroundColor: isSelected ? getColorByRating(user.avgRating) : 'background.dark',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                          }}
                        >
                          {isSelected && (
                            <Typography sx={{ fontSize: { xs: 9, sm: 10 }, fontWeight: 500, color: 'primary.contrastText' }}>
                              {user.avgRating.toFixed(2)}
                            </Typography>
                          )}
                        </AppBox>
                      )
                    })}
                  </AppBox>
                </TableCell>
                <TableCell><Typography sx={{ fontSize: { xs: 11, sm: 12 } }}>{user.expectedRating.toFixed(2)}</Typography></TableCell>
                <TableCell>
                  <Tooltip title={gap <= 0 ? `${pageTranslation.exceedsBy()} ${Math.abs(gap).toFixed(2)} ${pageTranslation.stars()}` : `${pageTranslation.belowTargetBy()} ${gap.toFixed(2)} ${pageTranslation.stars()}`} arrow>
                    <Typography sx={{ fontSize: { xs: 11, sm: 12 }, fontWeight: 600, color: gapColor }}>
                      {gap <= 0 ? `+${Math.abs(gap).toFixed(2)}` : `+${gap.toFixed(2)}`}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <Tooltip title={status} arrow>
                    <Chip
                      label={status}
                      size="small"
                      icon={<Typography sx={{ fontSize: { xs: 12, sm: 14 } }}>⚠</Typography>}
                      sx={{
                        fontSize: { xs: 9, sm: 10 },
                        backgroundColor: `${gapColor}20`,
                        color: gapColor,
                        '& .MuiChip-icon': { color: gapColor },
                        borderRadius: '10px',
                        width: { xs: '10em', sm: '12em' },
                        justifyContent: 'flex-start',
                      }}
                    />
                  </Tooltip>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </AppBox>
  )
})

const styles = {
  tableContainer: {
    width: '100%',
    overflowX: 'auto',
    '& .MuiTable-root': {
      minWidth: { xs: 650, sm: 'auto' },
    },
  },
  emptyStateContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    flex: 1,
    padding: { xs: 2, sm: 3 },
  },
  emptyText: {
    fontSize: 12,
    color: 'text.secondary',
    textAlign: 'center',
  },
}
